import { TextField } from '@calefy-inc/informedMaterial';
import Typography from '@mui/material/Typography';
import { Link } from '@reach/router';
import Fade from '@mui/material/Fade';
import SearchIcon from '@mui/icons-material/Search';
import { useRef, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { graphql } from '../../../gql';
import { useAuth } from '@calefy-inc/authentication';
import { removeWarning, addWarning } from '../../../store/QuoteWizardState';
import { useDispatch } from 'react-redux';

import { Component } from './classes';
import { useFormApi } from 'informed';
import { useSelector } from 'react-redux';
import { List, ListItemButton, ListItemText } from '@mui/material';

// types
import { QuoteComponentProps } from './types';
import { StoreState } from '../../../store';

const SEARCH_TIMEOUT_INTERVAL = 600;
interface PreviousQuote {
  uniqueID: string;
  friendlyID: string;
  businessName: string;
}

function ProgramBuilderComponent() {
  return (
    <>
      <TextField name='defaultValue' label='Default Value' />
      <Typography variant='caption' sx={{ my: 2 }} color='textSecondary'>
        Textfield Question Prompts longer than 79 characters can cause visual
        discrepencies between textfields.
      </Typography>
    </>
  );
}

const QuoteComponent = function ({
  questionInstance,
  classes,
  ...props
}: QuoteComponentProps) {
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchCompanyName, { data }] = useLazyQuery(SEARCH_COMPANY_NAME);
  const [companyName, setCompanyName] = useState<string>('');
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [previousQuotes, setPreviousQuotes] = useState<PreviousQuote[]>([]);

  const { token } = useAuth();
  const dispatch = useDispatch();
  const formApi = useFormApi();
  const fieldName = questionInstance.generateFieldName();
  const currentQuoteUUID = useSelector(
    (state: StoreState) => state.quoteWizard.currentQuoteUUID,
  );

  const debouncedSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }

    if (e?.target?.value) {
      setCompanyName(e.target.value.trim());

      const timerId = setTimeout(() => {
        searchCompanyName({
          variables: {
            name: e.target.value,
            token,
            isFuzzyMatching: true,
            ...(currentQuoteUUID ? { quoteUuid: currentQuoteUUID } : {}),
          },
        });
      }, SEARCH_TIMEOUT_INTERVAL);

      timerIdRef.current = timerId;
    }
  };

  useEffect(() => {
    const key = companyName;

    if (
      data?.lookupDoesNameInsuredExist &&
      data.lookupDoesNameInsuredExist.length > 0
    ) {
      let uniqueQuotes: PreviousQuote[] = [];
      data.lookupDoesNameInsuredExist.forEach((quote) => {
        uniqueQuotes.push({
          uniqueID: quote?.uniqueID ?? '',
          businessName: quote?.structuredData?.businessName ?? '',
          friendlyID: quote?.friendlyId ?? '',
        });
      });

      const uniqueQuotesExcludingCurrent = uniqueQuotes.filter(
        (quote) => quote?.uniqueID !== currentQuoteUUID,
      );
      if (uniqueQuotesExcludingCurrent.length > 0) {
        const quoteString =
          uniqueQuotesExcludingCurrent.length >= 2 ? 'quotes' : 'quote';
        const article = uniqueQuotesExcludingCurrent.length >= 2 ? 'the' : 'a';

        setWarningMessage(
          `\"${companyName}\" has the same or a similar company name associated with ${article} ${quoteString}`,
        );
        setShowWarning(true);
        setPreviousQuotes(uniqueQuotesExcludingCurrent);

        dispatch(
          addWarning({
            key,
            warning: {
              info: {
                type: 'company name already insured',
                companyName: companyName,
              },
              quote_unique_ids: uniqueQuotesExcludingCurrent.map(
                (quote) => quote.uniqueID,
              ),
            },
          }),
        );
      }
    } else {
      setShowWarning(false);
      dispatch(
        removeWarning({
          key,
        }),
      );
    }
  }, [data]);

  // NB This is to search on initial load
  useEffect(() => {
    if (token && formApi) {
      const companyName = formApi.getValue(fieldName);
      if (!companyName || typeof companyName !== 'string') {
        return;
      }
      searchCompanyName({
        variables: {
          name: companyName,
          token,
          isFuzzyMatching: true,
          ...(currentQuoteUUID ? { quoteUuid: currentQuoteUUID } : {}),
        },
      });
      setCompanyName(companyName);
    }
  }, [token, formApi, currentQuoteUUID]);

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <TextField
        label={questionInstance.getQuoteQuestionLabel()}
        name={questionInstance.apiName}
        id={questionInstance.id}
        helperText={questionInstance.helpText}
        required={questionInstance.required}
        className={classes && classes.textfield ? classes.textfield : ''}
        classes={classes}
        {...props}
        variant='standard'
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSearch(e)
        }
      />
      <Fade in={showWarning} timeout={300}>
        <div>
          {showWarning && (
            <Typography
              variant='subtitle1'
              color='red'
              sx={{ display: 'block', mt: 1 }}
            >
              Warning: {warningMessage}{' '}
              <List>
                {previousQuotes.map((quote) => (
                  <ListItemButton
                    key={quote.uniqueID}
                    component={Link}
                    to={`/insurtech/manager/quotes/details/${quote.uniqueID}`}
                    sx={{
                      textDecoration: 'underline',
                      '&:hover': {
                        textDecoration: 'underline',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        `${quote.businessName}: ` +
                        (quote.friendlyID !== ''
                          ? quote.friendlyID
                          : quote.uniqueID)
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Typography>
          )}
        </div>
      </Fade>
    </>
  );
};

const SEARCH_COMPANY_NAME = graphql(`
  query lookupDoesNameInsuredExist(
    $name: String!
    $token: String!
    $isFuzzyMatching: Boolean = false
    $quoteUuid: String
  ) {
    lookupDoesNameInsuredExist(
      name: $name
      token: $token
      isFuzzyMatching: $isFuzzyMatching
      quoteUuid: $quoteUuid
    ) {
      friendlyId
      uniqueID
      structuredData {
        businessName
      }
    }
  }
`);

const IconComponent = (): JSX.Element => {
  return <SearchIcon fontSize='small' color='primary' />;
};

export default function createComponentRegistry() {
  return new Component({
    type: 'vago-textfield-search-company-name',
    typeLabel: 'Vago Text Field Search For Existing Company Name',
    quoteComponent: QuoteComponent,
    dataType: 'string',
    programBuilderComponent: ProgramBuilderComponent,
    icon: IconComponent,
  });
}

export { QuoteComponent as quoteComponent };
